/**
 * 排序入口方法（按 type 分发）
 *
 * - createSortFunctionByType: 根据 type 和统一 options 选择合适的工厂方法，生成可复用的排序函数
 * - sortArrayByType: 根据 type 和统一 options 直接对数组进行排序
 *
 * 适用于希望由调用方以较高层级的「type + options」来选择排序策略的场景。
 */

import {
  TSortOptions,
} from './types';
import {
  sortByPrimaryValue,
} from './flatSort';
import {
  sortByGroupedPrimaryKey,
  sortByGroupedPrimaryKeyAdvanced,
} from './groupedSort';
import {
  createOptimizedAdvancedSortFunction,
  createOptimizedSortFunction,
} from './factories';

/**
 * 根据 type 选择并创建对应的排序函数
 * - flat: 返回基于 sortByPrimaryValue 的排序函数
 * - grouped: 返回基于 createOptimizedSortFunction 的排序函数
 * - grouped-advanced: 返回基于 createOptimizedAdvancedSortFunction 的排序函数
 */
export function createSortFunctionByType<T>(
  options: TSortOptions<T>,
): (items: T[]) => T[] {
  switch (options.type) {
    case 'flat': {
      // 对于 flat 模式，直接返回一个包装了 sortByPrimaryValue 的函数
      return (items: T[]): T[] => sortByPrimaryValue(items, options);
    }
    case 'grouped': {
      // 兜底检查必需配置
      if (!options.groupKey) {
        // groupKey 类型上必填，这里是 runtime 兜底（防止 JS 使用）
        // eslint-disable-next-line no-console
        console.warn(
          '[createSortFunctionByType][grouped] groupKey is missing, return identity function.',
        );
        return (items: T[]): T[] => items;
      }
      return createOptimizedSortFunction<T>(options);
    }
    case 'grouped-advanced': {
      if (!options.groupKey) {
        // eslint-disable-next-line no-console
        console.warn(
          '[createSortFunctionByType][grouped-advanced] groupKey is missing, return identity function.',
        );
        return (items: T[]): T[] => items;
      }
      return createOptimizedAdvancedSortFunction<T>(options);
    }
    default: {
      // eslint-disable-next-line no-console
      console.warn(
        '[createSortFunctionByType] Unknown sort type, return identity function. type =',
        (options as any).type,
      );
      return (items: T[]): T[] => items;
    }
  }
}

/**
 * 根据 type 直接对数组进行排序
 * - 内部不会调用工厂函数，而是直接调用具体的排序实现
 */
export function sortArrayByType<T>(
  items: T[],
  options: TSortOptions<T>,
): T[] {
  if (!Array.isArray(items)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[sortArrayByType] expects an array, received:',
      typeof items,
    );
    return [];
  }
  if (items.length <= 1) {
    return items;
  }

  switch (options.type) {
    case 'flat':
      return sortByPrimaryValue(items, options);
    case 'grouped': {
      if (!options.groupKey) {
        // eslint-disable-next-line no-console
        console.warn(
          '[sortArrayByType][grouped] groupKey is missing, return original array.',
        );
        return items;
      }
      return sortByGroupedPrimaryKey(items, options);
    }
    case 'grouped-advanced': {
      if (!options.groupKey) {
        // eslint-disable-next-line no-console
        console.warn(
          '[sortArrayByType][grouped-advanced] groupKey is missing, return original array.',
        );
        return items;
      }
      return sortByGroupedPrimaryKeyAdvanced(items, options);
    }
    default:
      // eslint-disable-next-line no-console
      console.warn(
        '[sortArrayByType] Unknown sort type, return original array. type =',
        (options as any).type,
      );
      return items;
  }
}


